import { IAlarm, SOURCE, StatusInfo } from '@alert-manager/lib';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { AlarmFilters, AlarmsResponse, getAlarms, getEnvCounts } from '../api/alarms';
import { statusAPI } from '../api/status';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import AlarmList from './AlarmList';
import FilterControls from './FilterControls';
import LogsPage from './LogsPage';
import SettingsPage from './SettingsPage';
import Sidebar, { NavPage } from './Sidebar';

interface FilterState {
  environment: string;
  source: string;
  status: string;
  severity: string;
  project: string;
  timeRange: string;
  acknowledgedStatus: string;
}

interface EnvCounts {
  total: number;
  critical: number;
  ongoing: number;
}

const ENV_CONFIG = {
  production: { label: 'Production', color: 'red', icon: '🔴' },
  staging: { label: 'Staging', color: 'amber', icon: '🟡' },
  develop: { label: 'Develop', color: 'cyan', icon: '🔵' },
} as const;

const envCardStyles = {
  production: {
    labelColor: '#ef4444',
    badge: 'bg-red-500 text-white',
    activeDark: 'bg-red-950/60 border-red-500/60 ring-1 ring-red-500/30',
    activeLight: 'bg-red-50 border-red-400 ring-1 ring-red-300/50',
  },
  staging: {
    labelColor: '#f59e0b',
    badge: 'bg-amber-500 text-black',
    activeDark: 'bg-amber-950/60 border-amber-500/60 ring-1 ring-amber-500/30',
    activeLight: 'bg-amber-50 border-amber-400 ring-1 ring-amber-300/50',
  },
  develop: {
    labelColor: '#06b6d4',
    badge: 'bg-cyan-500 text-black',
    activeDark: 'bg-cyan-950/60 border-cyan-500/60 ring-1 ring-cyan-500/30',
    activeLight: 'bg-cyan-50 border-cyan-400 ring-1 ring-cyan-300/50',
  },
} as const;

const Dashboard: React.FC = () => {
  const [alarmsData, setAlarmsData] = useState<AlarmsResponse>({
    alarms: [],
    pagination: { page: 1, limit: 50, total: 0, totalPages: 0, hasNext: false, hasPrev: false },
  });
  const [filters, setFilters] = useState<FilterState>({
    environment: 'all',
    source: 'all',
    status: 'all',
    severity: 'all',
    project: '',
    timeRange: '48h',
    acknowledgedStatus: 'unacknowledged',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const initialLoadDone = useRef(false);
  const countsLoadedOnce = useRef(false);
  const [countsLoading, setCountsLoading] = useState(true);
  const [_status, setStatus] = useState<StatusInfo | null>(null);
  const [envCounts, setEnvCounts] = useState<Record<string, EnvCounts>>({});
  const { logout, isAuthenticated } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [activePage, setActivePage] = useState<NavPage>('dashboard');

  const getTimeRange = useCallback((timeRange: string): { timeFrom: string; timeTo: string } | null => {
    if (timeRange === 'all') return null;
    const now = new Date();
    const timeTo = now.toISOString();
    const offsets: Record<string, number> = {
      '1h': 3600000,
      '6h': 21600000,
      '24h': 86400000,
      '48h': 172800000,
      '7d': 604800000,
      '30d': 2592000000,
    };
    const ms = offsets[timeRange] || 172800000;
    return { timeFrom: new Date(now.getTime() - ms).toISOString(), timeTo };
  }, []);

  const urlDefaults: Record<string, string> = {
    environment: 'all',
    source: 'all',
    status: 'all',
    severity: 'all',
    project: '',
    timeRange: '48h',
    acknowledgedStatus: 'unacknowledged',
  };

  const updateURL = useCallback((newFilters: FilterState, page: number) => {
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== urlDefaults[key]) params.set(key, value);
    });
    if (page > 1) params.set('page', page.toString());
    const newURL = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.history.replaceState(null, '', newURL);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setFilters({
      environment: urlParams.get('environment') || 'all',
      source: urlParams.get('source') || 'all',
      status: urlParams.get('status') || 'all',
      severity: urlParams.get('severity') || 'all',
      project: urlParams.get('project') || '',
      timeRange: urlParams.get('timeRange') || '48h',
      acknowledgedStatus: urlParams.get('acknowledgedStatus') || 'unacknowledged',
    });
    setCurrentPage(parseInt(urlParams.get('page') || '1', 10));
  }, []);

  // Load environment counts for the summary cards (single API call)
  const loadEnvCounts = useCallback(async () => {
    try {
      if (!countsLoadedOnce.current) setCountsLoading(true);
      const timeRange = getTimeRange(filters.timeRange);
      const counts = await getEnvCounts(timeRange?.timeFrom, timeRange?.timeTo);
      setEnvCounts(counts);
    } catch {
      // Non-critical, don't error
    } finally {
      setCountsLoading(false);
      countsLoadedOnce.current = true;
    }
  }, [filters.timeRange, getTimeRange]);

  const loadAlarms = useCallback(async () => {
    try {
      // Only show full loading spinner on initial load — background refreshes update silently
      if (!initialLoadDone.current) setLoading(true);
      setError('');

      const apiFilters: AlarmFilters = {};
      if (filters.environment !== 'all') apiFilters.environment = filters.environment;
      if (filters.source !== 'all') apiFilters.source = filters.source as SOURCE;
      if (filters.status !== 'all') apiFilters.status = filters.status as 'ongoing' | 'resolved';
      if (filters.severity !== 'all') apiFilters.severity = filters.severity;
      if (filters.acknowledgedStatus !== 'all') apiFilters.acknowledgedStatus = filters.acknowledgedStatus;
      if (filters.project && filters.project.trim() !== '') apiFilters.project = filters.project.trim();

      const timeRange = getTimeRange(filters.timeRange);
      if (timeRange) {
        apiFilters.timeFrom = timeRange.timeFrom;
        apiFilters.timeTo = timeRange.timeTo;
      }

      const fetchedData = await getAlarms(apiFilters, { page: currentPage, limit: 25 });
      setAlarmsData(fetchedData);
    } catch (error) {
      console.error('Failed to load alarms:', error);
      if (error instanceof Error && error.message.includes('401')) {
        logout();
      } else {
        setError('Failed to load alarms. Please try again.');
      }
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, [filters, currentPage, logout, getTimeRange]);

  const handleFilterChange = (filterType: keyof FilterState, value: string) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    setCurrentPage(1);
    updateURL(newFilters, 1);
  };

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      updateURL(filters, page);
    },
    [filters, updateURL],
  );

  useEffect(() => {
    loadAlarms();
  }, [filters, currentPage]);

  useEffect(() => {
    loadEnvCounts();
  }, [filters.timeRange]);

  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      setFilters({
        environment: urlParams.get('environment') || 'all',
        source: urlParams.get('source') || 'all',
        status: urlParams.get('status') || 'all',
        severity: urlParams.get('severity') || 'all',
        project: urlParams.get('project') || '',
        timeRange: urlParams.get('timeRange') || '48h',
        acknowledgedStatus: urlParams.get('acknowledgedStatus') || 'unacknowledged',
      });
      setCurrentPage(parseInt(urlParams.get('page') || '1', 10));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.target && (e.target as HTMLElement).tagName === 'INPUT') return;
      if (e.key === 'ArrowLeft' && alarmsData.pagination.hasPrev) {
        e.preventDefault();
        handlePageChange(alarmsData.pagination.page - 1);
      } else if (e.key === 'ArrowRight' && alarmsData.pagination.hasNext) {
        e.preventDefault();
        handlePageChange(alarmsData.pagination.page + 1);
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [alarmsData.pagination, handlePageChange]);

  useEffect(() => {
    statusAPI.getStatus().then(setStatus).catch(console.error);
  }, []);

  // Use refs for socket callbacks to avoid reconnecting on filter changes
  const loadAlarmsRef = useRef(loadAlarms);
  const loadEnvCountsRef = useRef(loadEnvCounts);
  useEffect(() => {
    loadAlarmsRef.current = loadAlarms;
  }, [loadAlarms]);
  useEffect(() => {
    loadEnvCountsRef.current = loadEnvCounts;
  }, [loadEnvCounts]);

  useEffect(() => {
    if (!isAuthenticated) return;
    // websocket-only: HTTP long-polling would need sticky sessions when running multiple replicas
    const socket = io({ path: '/ws', transports: ['websocket'] });
    let debounceTimer: ReturnType<typeof setTimeout>;
    socket.on('reloadAlarms', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        loadAlarmsRef.current();
        loadEnvCountsRef.current();
      }, 300);
    });
    return () => {
      clearTimeout(debounceTimer);
      socket.disconnect();
    };
  }, [isAuthenticated]);

  // Compute severity distribution for current view
  const severityDist = useMemo(() => {
    const dist = { critical: 0, high: 0, warning: 0, info: 0 };
    alarmsData.alarms.forEach((a: IAlarm) => {
      if (a.severity in dist) dist[a.severity as keyof typeof dist]++;
    });
    return dist;
  }, [alarmsData.alarms]);

  const totalOngoing = envCounts.production?.ongoing + envCounts.staging?.ongoing + envCounts.develop?.ongoing || 0;

  const paginBtnClass = isDark
    ? 'px-2 py-1 text-xs rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-slate-300 hover:text-white hover:bg-white/10'
    : 'px-2 py-1 text-xs rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-slate-600 hover:text-slate-900 hover:bg-slate-100';

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--surface)', color: 'var(--text-primary)' }}>
      <Sidebar activePage={activePage} onNavigate={setActivePage} />

      {activePage === 'logs' && <LogsPage onClose={() => setActivePage('dashboard')} embedded />}
      {activePage === 'settings' && <SettingsPage onClose={() => setActivePage('dashboard')} embedded />}

      {activePage === 'dashboard' && (
        <main className="flex-1 flex flex-col overflow-auto">
          {/* Environment Summary Cards */}
          <div className="px-4 md:px-6 pt-4 pb-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-5xl mx-auto">
              {/* All environments card */}
              <button
                onClick={() => {
                  const newFilters: FilterState = {
                    environment: 'all',
                    source: 'all',
                    status: 'ongoing',
                    severity: 'all',
                    project: '',
                    timeRange: filters.timeRange,
                    acknowledgedStatus: 'all',
                  };
                  setFilters(newFilters);
                  setCurrentPage(1);
                  updateURL(newFilters, 1);
                }}
                className={`rounded-xl border-2 p-4 transition-all cursor-pointer text-left ${
                  filters.environment === 'all'
                    ? isDark
                      ? 'border-slate-400 bg-slate-800 shadow-lg'
                      : 'border-slate-400 bg-white shadow-lg'
                    : isDark
                      ? 'border-slate-700 bg-slate-800/60 hover:bg-slate-800'
                      : 'border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300'
                }`}
              >
                <div className="text-[11px] uppercase tracking-wider font-bold mb-1" style={{ color: 'var(--text-secondary)' }}>
                  All Environments
                </div>
                <div className="flex items-baseline gap-2">
                  {countsLoading ? (
                    <div className="h-8 w-10 rounded animate-pulse" style={{ backgroundColor: 'var(--border)' }} />
                  ) : (
                    <>
                      <span className="text-3xl font-black" style={{ color: 'var(--text-primary)' }}>
                        {totalOngoing}
                      </span>
                      <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
                        active
                      </span>
                    </>
                  )}
                </div>
              </button>

              {/* Per-environment cards */}
              {(Object.keys(ENV_CONFIG) as Array<keyof typeof ENV_CONFIG>).map((env) => {
                const config = ENV_CONFIG[env];
                const counts = envCounts[env] || { total: 0, critical: 0, ongoing: 0 };
                const styles = envCardStyles[env];
                const isActive = filters.environment === env;
                const activeClass = isDark ? styles.activeDark : styles.activeLight;
                const inactiveClass = isDark
                  ? 'border-slate-700 bg-slate-800/60 hover:bg-slate-800'
                  : 'border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300';

                return (
                  <button
                    key={env}
                    onClick={() => {
                      const newFilters: FilterState = {
                        environment: env,
                        source: 'all',
                        status: 'ongoing',
                        severity: 'all',
                        project: '',
                        timeRange: filters.timeRange,
                        acknowledgedStatus: 'all',
                      };
                      setFilters(newFilters);
                      setCurrentPage(1);
                      updateURL(newFilters, 1);
                    }}
                    className={`rounded-xl border-2 p-4 transition-all cursor-pointer text-left ${
                      isActive ? activeClass + ' shadow-lg' : inactiveClass
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] uppercase tracking-wider font-bold" style={{ color: styles.labelColor }}>
                        {config.label}
                      </span>
                      {!countsLoading && counts.critical > 0 && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${styles.badge}`}>{counts.critical} CRIT</span>
                      )}
                    </div>
                    <div className="flex items-baseline gap-2">
                      {countsLoading ? (
                        <div className="h-8 w-10 rounded animate-pulse" style={{ backgroundColor: 'var(--border)' }} />
                      ) : (
                        <>
                          <span className="text-3xl font-black" style={{ color: 'var(--text-primary)' }}>
                            {counts.ongoing}
                          </span>
                          <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
                            active
                          </span>
                        </>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Filter Bar */}
          <div className="px-4 md:px-6 py-2">
            <div className="max-w-5xl mx-auto">
              <FilterControls filters={filters} onFilterChange={handleFilterChange} totalResults={alarmsData.pagination.total} />
            </div>
          </div>

          {/* Results header */}
          <div className="px-4 md:px-6 py-2">
            <div className="max-w-5xl mx-auto flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
                  {alarmsData.pagination.total === 0
                    ? 'No alarms'
                    : `${(alarmsData.pagination.page - 1) * alarmsData.pagination.limit + 1}–${Math.min(alarmsData.pagination.page * alarmsData.pagination.limit, alarmsData.pagination.total)} of ${alarmsData.pagination.total}`}
                </span>
                {/* Severity distribution mini-badges */}
                <div className="flex gap-1.5">
                  {severityDist.critical > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-500 font-bold">{severityDist.critical} critical</span>
                  )}
                  {severityDist.high > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-500 font-bold">{severityDist.high} high</span>
                  )}
                  {severityDist.warning > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-600 font-bold">{severityDist.warning} warn</span>
                  )}
                  {severityDist.info > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-500 font-bold">{severityDist.info} info</span>
                  )}
                </div>
              </div>

              {/* Pagination */}
              {alarmsData.pagination.totalPages > 1 && (
                <div className="flex gap-1 items-center">
                  <button
                    onClick={() => handlePageChange(alarmsData.pagination.page - 1)}
                    disabled={!alarmsData.pagination.hasPrev}
                    className={paginBtnClass}
                  >
                    ← Prev
                  </button>
                  <span className="px-2 text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
                    {alarmsData.pagination.page}/{alarmsData.pagination.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(alarmsData.pagination.page + 1)}
                    disabled={!alarmsData.pagination.hasNext}
                    className={paginBtnClass}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="px-4 md:px-6 pb-2">
              <div className="max-w-5xl mx-auto bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm text-red-500 font-medium">{error}</span>
                <button
                  onClick={() => loadAlarms()}
                  className="text-xs text-red-500 hover:text-red-400 px-2 py-1 hover:bg-red-500/10 rounded font-medium"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Alarm List */}
          <div className="flex-1 px-4 md:px-6 pb-6">
            <div className="max-w-5xl mx-auto">
              {loading ? (
                <div className="flex justify-center items-center py-16">
                  <div
                    className="animate-spin rounded-full h-6 w-6 border-2 border-t-transparent"
                    style={{ borderColor: 'var(--border)', borderTopColor: 'var(--text-muted)' }}
                  />
                  <span className="ml-3 text-sm" style={{ color: 'var(--text-muted)' }}>
                    Loading alarms...
                  </span>
                </div>
              ) : (
                <AlarmList
                  alarms={alarmsData.alarms}
                  onAlarmsUpdate={() => {
                    loadAlarms();
                    loadEnvCounts();
                  }}
                  pagination={alarmsData.pagination}
                  onPageChange={handlePageChange}
                />
              )}
            </div>
          </div>
        </main>
      )}
    </div>
  );
};

export default Dashboard;
